import { Component, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { InvoiceEnums } from 'src/app/enum/invoice-enums';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { InvoiceService } from 'src/app/services/invoicing/invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-info-invoice',
  templateUrl: './info-invoice.component.html',
  styleUrls: ['./info-invoice.component.scss'],
  standalone: false
})
export class InfoInvoiceComponent {
  accountingInterfaceCode: number | string = '';
  bussinessDocumentsTypeIsRental: string = '';
  codeNameTypeDIAN: string | undefined = '';
  cufe: string | null = '';
  enableSetInvoiceWalletType: boolean = false;
  formInvoiceInfo: FormGroup = new FormGroup({});
  invoiceModel: InvoiceModel = new InvoiceModel();
  invoiceOriginConsecutive: number | null = null;
  invoiceStateOpen: number = InvoiceEnums.abierta;
  listInvoiceOperations: CustomListDropdown[];
  listInvoiceWalletTypes: CustomListDropdown[];
  listPaymentMethods: any[] = [];
  listSellers: any[] = [];
  listTypeDIANs: any[] = [];
  listZones: any[] = [];
  movementCode: number | string = '';
  permissionUpdatable: boolean = false;
  sellerId: number = 0;
  suscription: Subscription;
  isUpdated: boolean = false;

  constructor(private invoiceService: InvoiceService,
    private formBuilder: FormBuilder,
    public config: DynamicDialogConfig,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef) {
    this.getAllLists();
    this.validateForm(this.invoiceModel);
  }

  async ngOnInit(): Promise<void> {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.suscription = input$?.subscribe((inputs: any) => {
      this.invoiceModel = inputs.invoiceModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.bussinessDocumentsTypeIsRental = inputs.bussinessDocumentsTypeIsRental;
      this.enableSetInvoiceWalletType = inputs.enableSetInvoiceWalletType;
      this.listSellers = inputs.listSellers;
      this.listZones = inputs.listZones;
      this.sellerId = inputs.sellerId;
    });
    await this.validateForm(this.invoiceModel);
    this.formInvoiceInfo.controls['sellerId'].patchValue(this.sellerId);
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  close() {
    const data: ModalObjectType = {};
    data.params = { updateInvoice: this.isUpdated };
    this.ref.close(data);
  }

  changeOperation(invoiceOperationId: number) {
    const map: Record<number, InvoiceEnums> = {
      [InvoiceEnums.contado]: InvoiceEnums.efectivo,
      [InvoiceEnums.credito]: InvoiceEnums.acuerdoMutuo
    };

    const value = this.listPaymentMethods.find(x => x.code === map[invoiceOperationId])?.value;
    this.formInvoiceInfo.get('conceptPaymentMethodId')?.patchValue(value);
  }

  async getAllLists(...listsToLoad: Array<'typeDIANs' | 'invoiceOperations' | 'paymentMethods' | 'invoiceWalletTypes'>) {
    try {
      const lists = {
        typeDIANs: async () => {
          this.listTypeDIANs = await firstValueFrom(
            this.invoiceService.getAllTypeDIANForSelectAsync()
          );
        },
        invoiceOperations: async () => {
          this.listInvoiceOperations = await firstValueFrom(
            this.invoiceService.getAllInvoiceOperationsForSelectAsync()
          );
        },
        paymentMethods: async () => {
          this.listPaymentMethods = await firstValueFrom(
            this.invoiceService.getAllPaymentMethodsForSelectAsync()
          );
        },
        invoiceWalletTypes: async () => {
          this.listInvoiceWalletTypes = await firstValueFrom(
            this.invoiceService.getAllInvoiceWalletTypeForSelectAsync()
          );
        },
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.typeDIANs(),
          lists.invoiceOperations(),
          lists.paymentMethods(),
          lists.invoiceWalletTypes(),
        ]);
      }
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getAccountingInterfaceCode(accountingInterfaceId: number | null) {
    this.accountingInterfaceCode = !accountingInterfaceId
      ? ""
      : await firstValueFrom(this.invoiceService.getAccountingInterfaceCode(accountingInterfaceId));
  }

  async getZone(zoneId: number | null) {
    const zone = this.listZones.find(x => x.value === zoneId);

    this.formInvoiceInfo.patchValue({
      zoneId: zone ? zoneId : null
    });
  }


  async getMovementCode(movementId: number | null) {
    this.movementCode = !movementId
      ? ""
      : await firstValueFrom(this.invoiceService.getMovementCodeById(movementId));
  }

  async send() {
    if (this.formInvoiceInfo.valid) {
      const modelInvoice = this.formInvoiceInfo.value as InvoiceModel;
      this.invoiceModel.zoneId = modelInvoice.zoneId;
      this.invoiceModel.sellerId = modelInvoice.sellerId;
      this.invoiceModel.invoiceOperationId = modelInvoice.invoiceOperationId;
      this.invoiceModel.conceptPaymentMethodId = modelInvoice.conceptPaymentMethodId;
      this.invoiceModel.invoiceWalletTypeId = modelInvoice.invoiceWalletTypeId;
      try {
        await firstValueFrom(this.invoiceService.updateInvoice(this.invoiceModel));
        this.toastHandlerService.showToast('success', 'La Factura fue actualizada exitosamente.');
        this.isUpdated = true;
      } catch (error) {
        console.error('Error al actualizar la factura desde información modal.', error);
      }
    }
  }

  setInvoiceWalletType() {
    Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea cambiar el tipo de cartera para esta factura?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          this.invoiceModel = await firstValueFrom(this.invoiceService.setInvoiceWalletType(this.invoiceModel.invoiceId));
          this.toastHandlerService.showToast('success', 'El tipo de cartera de la factura fue modificada exitosamente.');
          this.validateForm(this.invoiceModel);
        } catch (error) {
          console.error('Error al modificar el tipo de cartera de la factura.', error);
        }
      }
    });
  }

  async setTypeDIAN(conceptTypeDIANId: number) {
    if (this.listTypeDIANs.length == 0) {
      await Promise.all([
        this.getAllLists('typeDIANs')
      ]);
    }

    const typeDian = this.listTypeDIANs.find(x => x.value == conceptTypeDIANId);
    if (typeDian) {
      this.codeNameTypeDIAN = typeDian.label;
    }
  }

  async validateForm(invoice: InvoiceModel) {
    this.formInvoiceInfo = this.formBuilder.group({
      invoiceOperationId: [invoice.invoiceOperationId, [Validators.required, Validators.min(1)]],
      swCopied: [invoice.swCopied, []],
      swManual: [invoice.swManual, []],
      zoneId: [invoice.zoneId, [Validators.required, Validators.min(1)]],
      sellerId: [invoice.sellerId, [Validators.required, Validators.min(1)]],
      conceptPaymentMethodId: [invoice.conceptPaymentMethodId, [Validators.required,]],
      invoiceWalletTypeId: [invoice.invoiceWalletTypeId, [Validators.required, Validators.min(1)]],
    });

    this.invoiceOriginConsecutive = invoice.invoiceOriginConsecutive;
    this.cufe = invoice.cufe;
    if (invoice.conceptTypeDIANId != 0) await this.setTypeDIAN(invoice.conceptTypeDIANId);
    if (invoice.accountingInterfaceId != null) this.getAccountingInterfaceCode(invoice.accountingInterfaceId);
    if (invoice.movementId != null) this.getMovementCode(invoice.movementId);
    this.invoiceModel.invoiceStateId == this.invoiceStateOpen ? setTimeout(() => this.formInvoiceInfo.enable(), 100) :
      setTimeout(() => this.formInvoiceInfo.disable(), 100);
    setTimeout(() => this.formInvoiceInfo.controls["swCopied"].disable(), 100);
    setTimeout(() => this.formInvoiceInfo.controls["swManual"].disable(), 100);
  }

}
